import { useState } from 'react';
import type { FormLanguage } from '@chat-emp/shared';

import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import {
  CATEGORY_LABEL,
  matchesTemplate,
  TEMPLATE_CATEGORIES,
  templatesIn,
} from '../data/forms.js';
import type { Form } from '../fixtures/domain.js';
import { plural } from '../util/format.js';
import { LanguageSwitch } from './LanguageSwitch.js';
import styles from './FormTemplatesPanel.module.css';

/** A SHELF beside the work, not the work: shut until asked for, since templates are
 * for the moment a form is being written. Not how a form is STARTED - that press
 * opens the gallery. Templates only, and a press takes a copy, unwritten. */
export function FormTemplatesPanel({
  templates,
  loading = false,
  error = false,
  language,
  onLanguage,
  guarded,
  onPick,
  onClose,
}: {
  templates: Form[];
  loading?: boolean;
  error?: boolean;
  language: FormLanguage;
  onLanguage: (language: FormLanguage) => void;
  /** The panel does not know what is in the builder, only that there is something to
   * lose, which is all the question needs. */
  guarded: boolean;
  onPick: (template: Form) => void;
  onClose: () => void;
}) {
  const [confirming, setConfirming] = useState<Form | null>(null);
  const [query, setQuery] = useState('');
  const press = (template: Form) => {
    if (guarded) {
      setConfirming(template);
      return;
    }
    onPick(template);
  };

  const groups = TEMPLATE_CATEGORIES.map((category) => ({
    category,
    items: templatesIn(templates, language, category).filter((template) =>
      matchesTemplate(template, query),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <section className={styles.panel} aria-label="Templates">
      <div className={styles.header}>
        <h2 className={styles.title}>Templates</h2>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close templates"
        >
          <Icon name="close" size={18} />
        </button>
      </div>

      <div className={styles.toolbar}>
        <label className={styles.search}>
          <Icon name="search" size={16} />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <LanguageSwitch value={language} onChange={onLanguage} />
      </div>

      {/* The consequence, then the two ways out: the pane's delete confirm,
          in the one place a press here can cost anything. */}
      {confirming && (
        <div className={styles.confirm}>
          <p className={styles.confirmText}>
            Start over from {confirming.name}? What you have written is lost.
          </p>
          <div className={styles.confirmActions}>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const template = confirming;
                setConfirming(null);
                onPick(template);
              }}
            >
              Start over
            </Button>
          </div>
        </div>
      )}

      <div className={styles.body}>
        {groups.length === 0 && (
          <p className={styles.empty}>
            {query.trim() ? (
              `Nothing matches “${query.trim()}”.`
            ) : loading || error ? (
              error ? (
                'Templates could not be loaded.'
              ) : (
                'Loading templates…'
              )
            ) : (
              <>
                <span>Not yet available in this language.</span>
                {language !== 'en' && templatesIn(templates, 'en').length > 0 && (
                  <button
                    type="button"
                    className={styles.emptyAction}
                    onClick={() => onLanguage('en')}
                  >
                    Show them in English
                  </button>
                )}
              </>
            )}
          </p>
        )}
        {groups.map((group) => (
          <div key={group.category} className={styles.group}>
            <h3 className={styles.groupTitle}>{CATEGORY_LABEL[group.category].name}</h3>
            <ul className={styles.list}>
              {group.items.map((template) => (
                <li key={template.id}>
                  <button type="button" className={styles.card} onClick={() => press(template)}>
                    <span className={styles.cardHead}>
                      <span className={styles.name}>{template.name}</span>
                      <span className={styles.meta}>
                        {plural(template.fields.length, 'question')}
                      </span>
                    </span>
                    <span className={styles.questions}>
                      {template.fields.map((field, index) => (
                        <span key={field.id} className={styles.line}>
                          <span className={styles.lineNumber}>{index + 1}</span>
                          <span className={styles.lineText}>{field.label}</span>
                        </span>
                      ))}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
